import React, { useEffect, useReducer, createContext, Dispatch } from 'react';
import { KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN } from '../constant';
import { AuthAction, AuthState, AuthUser } from '../../interfaces/Contexts';
import { ReactChildren } from '../../interfaces/Common';
import storage from '../../storageService';
import { Logout } from '../../Utils/apiService';

// Initial State
export const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  search: null,
  isLoading: true,
};

// Reducer
const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN': {
      const userPayload: any = action.payload || { user: null };
      const accessTokenString =
        userPayload.user?.accessToken?.token?.token ||
        userPayload.user?.accessToken?.token ||
        '';
      const refreshTokenString =
        userPayload.user?.refreshToken?.token?.token ||
        userPayload.user?.refreshToken?.token ||
        '';

      const normalizedUser = userPayload.user
        ? {
            ...userPayload.user,
            accessToken: { token: accessTokenString },
            refreshToken: { token: refreshTokenString },
          }
        : null;

      // Save tokens and user to storage
      storage.set(KEY_ACCESS_TOKEN, accessTokenString);
      storage.set(KEY_REFRESH_TOKEN, refreshTokenString);
      storage.set('user', JSON.stringify(normalizedUser));
      localStorage.setItem('user', JSON.stringify(normalizedUser));

      return {
        ...state,
        isAuthenticated: !!normalizedUser,
        user: normalizedUser,
        isLoading: false,
      };
    }

    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        search: null,
        isLoading: false,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload?.isLoading ?? false,
      };

    case 'RESTORE_AUTH':
      return {
        ...state,
        isAuthenticated: !!action.payload?.user,
        user: action.payload?.user || null,
        isLoading: false,
      };

    default:
      return state;
  }
};
export const AuthContext = createContext<{
  state: AuthState;
  dispatch: Dispatch<AuthAction>;
  logoutUser: () => Promise<void>; 
}>({
  state: initialState,
  dispatch: () => {},
  logoutUser: async () => {}, 
});

// Provider
export const AuthContextProvider: React.FunctionComponent<ReactChildren> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

const logoutUser = async () => {
  try {
    const user = await storage.get('user');
    const parsedUser: AuthUser = user ? JSON.parse(user) : null;

    if (parsedUser?.applicantId) {
      const logoutTime = new Date().toISOString(); 

      await Logout({
        contactId: parsedUser.applicantId,
        logoutTime: logoutTime,
      });
    }

    await storage.remove(KEY_ACCESS_TOKEN);
    await storage.remove(KEY_REFRESH_TOKEN);
    await storage.remove('user');
    localStorage.removeItem('user');

    dispatch({ type: 'LOGOUT' });
  } catch (error) {
    console.error('Error while logging out:', error);
    dispatch({ type: 'LOGOUT' });
  }
};


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: { isLoading: true } });
        await new Promise((resolve) => setTimeout(resolve, 100)); 

        const user = await storage.get('user');
        const accessToken = await storage.get(KEY_ACCESS_TOKEN);
        const refreshToken = await storage.get(KEY_REFRESH_TOKEN);

        if (user && accessToken && refreshToken) {
          try {
            const parsedUser: AuthUser = JSON.parse(user);
            if (parsedUser.accessToken?.token && parsedUser.refreshToken?.token) {
              dispatch({ type: 'RESTORE_AUTH', payload: { user: parsedUser } });
            } else {
              dispatch({ type: 'LOGOUT' });
            }
          } catch (parseError) {
            console.error('Failed to parse user data', parseError);
            dispatch({ type: 'LOGOUT' });
          }
        } else {
          dispatch({ type: 'RESTORE_AUTH', payload: { user: null } });
        }
      } catch (error) {
        console.error('Failed to fetch user data', error);
        dispatch({ type: 'RESTORE_AUTH', payload: { user: null } });
      }
    };

    fetchUserData();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        logoutUser, 
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
